"use client";
import { useEffect, useState, useRef } from "react";
import {
  CalendarDays,
  Moon,
  Users,
  EditIcon,
  Gift,
  ExternalLink,
} from "lucide-react";
import DOMPurify from "dompurify";

import { useHolidayQuoteBrand } from "./contexts/use-holiday-quote-brand";
import { usePrimaryColorStyles } from "./contexts/use-primary-color-styles";
import { useQuoteTheme } from "./quote-theme/use-quote-theme";
import { iHolidayQuoteX } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";
import { cn } from "@/lib/utils";
import { formatConsistentDate } from "@/utils/timezone-date-utils";

type HolidayQuoteData = iHolidayQuoteX["data"];
interface QuoteGreetingsProps extends HolidayQuoteData {
  customerName: string;
  quoteCreatedDate: number;
  quoteId: string;
  isEditable: boolean;
  onEdit: () => void;
}

const USE_VIDEO:boolean = false;

export function QuoteGreetings({
  title,
  departureDate,
  nights,
  passengerCount,
  greetingText,
  bannerImages = ["/default-banner.jpg"], // Default is array with one image
  customerName,
  quoteId,
  isEditable,
  onEdit,
  quoteCreatedDate,
  dealLink,
  specialOccasion,
}: Omit<QuoteGreetingsProps, "primaryColor">) {
  const { primaryColor, headerTemplate, quoteTheme } = useHolidayQuoteBrand();
  const { containerStyle, textIconStyle } = usePrimaryColorStyles();
  const { classNames } = useQuoteTheme();
  const isWideHero =
    headerTemplate === "Wide" || quoteTheme.layoutConfig === "CONFIG_C";
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [sanitizedGreeting, setSanitizedGreeting] = useState("");
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });

  // Auto rotate every 5 seconds if there are multiple images (only when not using video)
  useEffect(() => {
    if (USE_VIDEO || bannerImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((current) =>
        current === bannerImages.length - 1 ? 0 : current + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [bannerImages.length]);

  // Add useEffect to sanitize HTML on client-side only
  useEffect(() => {
    setSanitizedGreeting(DOMPurify.sanitize(greetingText));
  }, [greetingText]);

  // Calculate video size dynamically based on container dimensions (only when using video)
  useEffect(() => {
    if (!USE_VIDEO) return;

    const container = videoContainerRef.current;
    if (!container) return;

    const updateVideoSize = () => {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      // For a square video to cover the container, use the larger dimension
      // This ensures the video covers the entire area
      const size = Math.max(containerWidth, containerHeight);

      setVideoSize({ width: size, height: size });
    };

    // Initial calculation
    updateVideoSize();

    // Use ResizeObserver to track container size changes
    const resizeObserver = new ResizeObserver(updateVideoSize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Format date to be more readable
  const formattedDate = formatConsistentDate(departureDate, "d MMMM yyyy");

  const quoteCreatedDateFormatted = new Date(
    quoteCreatedDate
  ).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="relative">
      <section className={classNames.greetingsCard}>
        {/* Banner Section - Video or Image Carousel */}
        <div
          ref={USE_VIDEO ? videoContainerRef : null}
          className={cn(
            "relative h-[200px] w-full overflow-hidden sm:h-[300px]",
            isWideHero ? classNames.heroBannerWide : classNames.heroBanner
          )}
        >
          {USE_VIDEO ? (
            /* Video Banner */
            <div className="absolute inset-0 w-full h-full">
              {videoSize.width > 0 && videoSize.height > 0 && (
                <iframe
                  src="https://www.youtube.com/embed/nhZvqVfxzwk?autoplay=1&mute=1&loop=1&playlist=nhZvqVfxzwk&controls=0&modestbranding=1&rel=0"
                  className="absolute top-1/2 left-1/2"
                  style={{
                    width: `${videoSize.width}px`,
                    height: `${videoSize.height}px`,
                    transform: "translate(-50%, -50%)",
                  }}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={`${title} - Video`}
                />
              )}
            </div>
          ) : (
            /* Image Carousel */
            <>
              {bannerImages.map((image, index) => (
                <div
                  key={image}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out
                        ${
                          index === currentImageIndex
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                >
                  <img
                    src={image}
                    alt={`${title} - Image ${index + 1}`}
                    className="object-cover h-[200px] sm:h-[300px] w-full"
                  />
                </div>
              ))}
            </>
          )}

          <div className="absolute inset-0">
            <div className="container h-full flex flex-col justify-between pb-4 sm:pb-6">
              <div className="bg-gradient-to-b from-black/0 to-black/60 bottom-0 absolute w-full">
                <div className="container mx-auto px-4 sm:px-6 mb-4">
                  <h1
                    className="mt-auto text-3xl font-bold text-white sm:text-4xl"
                    style={{ fontFamily: "var(--quote-font-heading)" }}
                  >
                    {/* Reduce font size if title is longer */}
                    <span className="sm:hidden">
                      {title.length > 50 ? (
                        <span className="text-2xl">{title}</span>
                      ) : (
                        <span className="text-3xl">{title}</span>
                      )}
                    </span>
                    <span className="hidden sm:inline">{title}</span>
                  </h1>
                </div>
                {/* Carousel Indicators - Only show if multiple images and not using video */}
                {!USE_VIDEO && bannerImages.length > 1 && (
                  <div className="flex gap-2 justify-center mt-2 sm:mt-4 mb-2">
                    {bannerImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all
                                ${
                                  index === currentImageIndex
                                    ? "bg-white w-4"
                                    : "bg-white/50"
                                }`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quote Details */}
        <div className="container mx-auto p-4 sm:p-6 relative">
          {isEditable && (
            <button
              onClick={onEdit}
              className="absolute right-2 sm:right-4 top-2 sm:top-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
              aria-label="Edit quote details"
            >
              <EditIcon className="w-4 h-4" />
            </button>
          )}

          {/* Quick Info Pills - More compact on mobile */}
          <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div
              className="flex items-center gap-1.5 rounded-full px-3 py-2 sm:gap-2 sm:px-4"
              style={{
                ...containerStyle,
                borderRadius: "var(--quote-radius-pill)",
              }}
            >
              <CalendarDays className="w-4 h-4" style={textIconStyle} />
              <span className="text-sm font-medium" style={textIconStyle}>
                <span className="hidden sm:inline">Departure: </span>
                <span className="sm:hidden">Dep: </span>
                {formattedDate}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 rounded-full px-3 py-2 sm:gap-2 sm:px-4"
              style={{
                ...containerStyle,
                borderRadius: "var(--quote-radius-pill)",
              }}
            >
              <Moon className="w-4 h-4" style={textIconStyle} />
              <span className="text-sm font-medium" style={textIconStyle}>
                <span className="hidden sm:inline">Duration: </span>
                <span className="sm:hidden">Dur: </span>
                {nights} Nights
              </span>
            </div>

            {/* Mobile: Fit passenger count in the same row */}
            <div
              className="flex items-center gap-1.5 rounded-full px-3 py-2 sm:gap-2 sm:px-4"
              style={{
                ...containerStyle,
                borderRadius: "var(--quote-radius-pill)",
              }}
            >
              <Users className="w-4 h-4" style={textIconStyle} />
              <span className="text-sm font-medium" style={textIconStyle}>
                {/* Mobile: Abbreviated labels */}
                <span className="sm:hidden">
                  {[
                    passengerCount.adults ? `${passengerCount.adults}A` : null,
                    passengerCount.youth ? `${passengerCount.youth}Y` : null,
                    passengerCount.children
                      ? `${passengerCount.children}C`
                      : null,
                    passengerCount.infants
                      ? `${passengerCount.infants}I`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>

                {/* Desktop: Full labels */}
                <span className="hidden sm:inline" style={textIconStyle}>
                  {[
                    passengerCount.adults
                      ? `${passengerCount.adults} Adults`
                      : null,
                    passengerCount.youth
                      ? `${passengerCount.youth} Youth`
                      : null,
                    passengerCount.children
                      ? `${passengerCount.children} Children`
                      : null,
                    passengerCount.infants
                      ? `${passengerCount.infants} Infants`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </span>
            </div>
          </div>

          {/* Customer Details - Stacked on mobile, grid on desktop */}
          <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-6 mb-4 sm:mb-6">
            {/* Mobile: Single line layout */}
            <div className="sm:hidden flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="text-sm font-medium text-gray-500">
                    Traveler
                  </h2>
                  <p className="text-base font-medium text-gray-900">
                    {customerName}
                  </p>
                </div>
                <div>
                  <h2 className="text-sm font-medium text-gray-500">Ref</h2>
                  <p className="text-base font-medium text-gray-900">
                    {/* Mobile: Replace QTE- with # */}
                    {quoteId.startsWith("QTE-")
                      ? `#${quoteId.substring(4)}`
                      : quoteId}
                  </p>
                </div>
                <div>
                  <h2 className="text-sm font-medium text-gray-500">Created</h2>
                  <p className="text-base font-medium text-gray-900">
                    {quoteCreatedDateFormatted}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop: Grid layout */}
            <div className="hidden sm:block space-y-2">
              <h2 className="text-sm font-medium text-gray-500">
                Lead Traveler's Name
              </h2>
              <p className="text-lg font-medium text-gray-900">
                {customerName}
              </p>
            </div>
            <div className="hidden sm:block space-y-2">
              <h2 className="text-sm font-medium text-gray-500">
                Quote Reference
              </h2>
              <p className="text-lg font-medium text-gray-900">{quoteId}</p>
            </div>
            <div className="hidden sm:block space-y-2">
              <h2 className="text-sm font-medium text-gray-500">
                Quote Created Date
              </h2>
              <p className="text-lg font-medium text-gray-900">
                {quoteCreatedDateFormatted}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="prose max-w-none">
            {/* Mobile: Use HtmlContentWithReadMore for compact display */}
            <div className="sm:hidden">
              <HtmlContentWithReadMore
                html={sanitizedGreeting || greetingText}
                maxWords={50}
                className="text-base text-gray-700 leading-relaxed"
              />
            </div>

            {/* Desktop: Regular HTML display */}
            <div className="hidden sm:block">
              <p className="text-base text-gray-700 leading-relaxed">
                {sanitizedGreeting ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: sanitizedGreeting }}
                  />
                ) : (
                  greetingText
                )}
              </p>
            </div>
          </div>

          {/* Footer Section - Deal Link & Special Occasion */}
          {(dealLink || specialOccasion) && (
            <>
              <div className="mt-6 sm:mt-8 border-t border-gray-200" />
              <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
                  {specialOccasion && (
                    <div className="flex items-center gap-2">
                      <Gift
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        style={{ color: primaryColor }}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        <span className="hidden sm:inline">
                          Special Occasion:{" "}
                        </span>
                        <span className="sm:hidden">Occasion: </span>
                        {specialOccasion}
                      </span>
                    </div>
                  )}

                  {dealLink && (
                    <a
                      href={dealLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <ExternalLink
                        className="w-4 h-4"
                        style={{ color: primaryColor }}
                      />
                      <span
                        className="text-sm font-medium"
                        style={{ color: primaryColor }}
                      >
                        View Deal
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
